import { Form } from 'react-router'
import { type ProviderName, enabledProviderNames } from '#app/utils/connections'
import { cn, useIsPending } from '#app/utils/misc'
import {
	providerRedirectTo,
	socialRedirectTarget,
} from '#app/utils/site/auth-ui'
import { siteContent } from '#app/utils/site/content'
import type { Audience } from '#app/utils/site/site-links'
import { AuthDivider } from './AuthBits'
import { AUTH_BUTTON_CLASS } from './AuthSubmit'
import { ProviderIcon } from './ProviderIcon'

const PROVIDER_CLASS: Record<ProviderName, string> = {
	google:
		'border border-tt-line bg-white font-medium text-tt-ink hover:bg-tt-page',
	apple: 'bg-black font-medium text-white hover:opacity-[.92]',
	facebook:
		'border border-tt-line bg-white font-medium text-tt-ink hover:bg-tt-page',
}

/**
 * "Or continue with" and one button per enabled provider (its
 * *_LOGIN_ENABLED switch, plus keys for Apple and Facebook); nothing at all
 * when every provider is switched off. Three across from
 * 601px, stacked on phones. Each button is a `<Form>` of its own that posts
 * to `/auth/:provider` with one hidden `redirectTo` (destination + userType,
 * plus the team invitation token), so render it after the main form, never
 * inside it. A co-applicant `invitation` becomes the destination, as it
 * does for the password login.
 */
export function SocialButtons({
	type,
	redirectTo,
	userType,
	invitation,
	teamInvite,
	className,
}: {
	type: 'Continue' | 'Login'
	redirectTo?: string | null
	userType: Audience
	invitation?: string | null
	teamInvite?: string | null
	className?: string
}) {
	const value = providerRedirectTo(
		socialRedirectTarget(redirectTo, invitation),
		userType,
		teamInvite,
	)
	const providers = enabledProviderNames()
	// Every *_LOGIN_ENABLED switch off: no divider, no empty row.
	if (providers.length === 0) return null
	return (
		<>
			<AuthDivider />
			<div
				className={cn(
					'grid grid-cols-1 gap-3 min-[601px]:auto-cols-fr min-[601px]:grid-flow-col',
					className,
				)}
			>
				{providers.map((provider) => (
					<ProviderButton
						key={provider}
						provider={provider}
						type={type}
						redirectTo={value}
					/>
				))}
			</div>
		</>
	)
}

function ProviderButton({
	provider,
	type,
	redirectTo,
}: {
	provider: ProviderName
	type: 'Continue' | 'Login'
	redirectTo: string
}) {
	const formAction = `/auth/${provider}`
	const isPending = useIsPending({ formAction })
	const lead =
		type === 'Login'
			? siteContent.auth.login.social
			: siteContent.auth.signup.social
	return (
		<Form method="POST" action={formAction}>
			<input type="hidden" name="redirectTo" value={redirectTo} />
			<button
				type="submit"
				aria-busy={isPending || undefined}
				disabled={isPending}
				className={cn(
					AUTH_BUTTON_CLASS,
					PROVIDER_CLASS[provider],
					'disabled:cursor-wait disabled:opacity-80',
				)}
			>
				<ProviderIcon provider={provider} />
				<span>
					<span className="sr-only">{lead} </span>
					{siteContent.auth.providers[provider]}
				</span>
			</button>
		</Form>
	)
}
